import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Stack,
  Alert,
  Divider,
  Button,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import SettingsIcon from "@mui/icons-material/Settings";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import api from "../../utils/api";

/* =========================================================
   HELPERS
   ========================================================= */
function getTrendMeta(trend, currentRank, previousRank) {
  const t = (trend || "").toUpperCase();

  if (t === "UP") {
    return {
      icon: "⬆️",
      label: previousRank
        ? `Up from #${previousRank} → #${currentRank}`
        : "Improved rank",
      color: "success",
    };
  }
  if (t === "DOWN") {
    return {
      icon: "⬇️",
      label: previousRank
        ? `Down from #${previousRank} → #${currentRank}`
        : "Dropped rank",
      color: "error",
    };
  }
  if (t === "SAME") {
    return {
      icon: "➡️",
      label: `No change (#${currentRank})`,
      color: "info",
    };
  }
  if (t === "NEW") {
    return {
      icon: "🆕",
      label: "New this week",
      color: "warning",
    };
  }
  return { icon: "➡️", label: "No data", color: "default" };
}

function fmt2(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x.toFixed(2) : "—";
}

/* =========================================================
   LEADERBOARD TABLE (ADMIN MONITOR)
   ========================================================= */
function LeaderboardTable({ rows }) {
  if (!rows || rows.length === 0) {
    return <Alert severity="info">No submissions for this week.</Alert>;
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell width={80}>Rank</TableCell>
          <TableCell>Student</TableCell>

          {/* NEW */}
          <TableCell width={90}>Trend</TableCell>

          <TableCell width={190}>Subject</TableCell>
          <TableCell width={120}>Best</TableCell>
          <TableCell width={120}>Avg</TableCell>

          {/* NEW */}
          <TableCell width={150}>Weighted</TableCell>

          <TableCell width={90}>Exams</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {rows.map((row) => {
          const rank = row.current_rank ?? row.rank;
          const trendMeta = getTrendMeta(
            row.trend,
            rank,
            row.previous_rank
          );

          return (
            <TableRow key={`${row.student_id}-${rank}`} hover>
              <TableCell>
                <Typography fontWeight={900}>#{rank}</Typography>
              </TableCell>

              <TableCell>
                <Typography fontWeight={700}>{row.full_name}</Typography>
              </TableCell>

              {/* TREND */}
              <TableCell>
                <Tooltip title={trendMeta.label}>
                  <Chip
                    label={trendMeta.icon}
                    size="small"
                    color={trendMeta.color}
                    variant="outlined"
                    sx={{ fontWeight: 900 }}
                  />
                </Tooltip>
              </TableCell>

              {/* SUBJECT */}
              <TableCell>
                {row.subject_name ? (
                  <Chip
                    label={`${row.subject_name}${
                      row.subject_code ? ` (${row.subject_code})` : ""
                    }`}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 900 }}
                  />
                ) : (
                  "—"
                )}
              </TableCell>

              {/* BEST */}
              <TableCell>
                <Chip
                  label={`${row.best_score ?? "—"}%`}
                  color="success"
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 900 }}
                />
              </TableCell>

              {/* AVG */}
              <TableCell>
                <Chip
                  label={`${row.avg_score ?? "—"}%`}
                  color="info"
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 900 }}
                />
              </TableCell>

              {/* WEIGHTED */}
              <TableCell>
                <Chip
                  label={fmt2(row.weighted_score)}
                  color="primary"
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 900 }}
                />
              </TableCell>

              {/* EXAMS */}
              <TableCell>
                <Chip
                  label={row.exams_taken ?? 0}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 900 }}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/* =========================================================
   SUBJECT INCLUSION SETTINGS
   ========================================================= */
function SubjectInclusionSettings({ subjects, savingMap, onToggle }) {
  if (!subjects || subjects.length === 0) {
    return <Alert severity="info">No subjects found.</Alert>;
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell width={80}>ID</TableCell>
          <TableCell>Subject</TableCell>
          <TableCell width={120}>Code</TableCell>
          <TableCell width={160}>Status</TableCell>
          <TableCell width={260}>Include in All-Subjects Challenge</TableCell>
          <TableCell width={170}>Last Updated</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {subjects.map((s) => {
          const saving = !!savingMap[s.id];

          return (
            <TableRow key={s.id} hover>
              <TableCell>{s.id}</TableCell>
              <TableCell>
                <Typography fontWeight={800}>{s.name}</Typography>
              </TableCell>
              <TableCell>
                <Chip label={s.code} size="small" variant="outlined" />
              </TableCell>
              <TableCell>
                <Chip
                  label={s.status ? "Active" : "Disabled"}
                  size="small"
                  color={s.status ? "success" : "default"}
                  variant="outlined"
                />
              </TableCell>

              <TableCell>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title="If OFF, this subject is excluded from the All-Subjects weekly challenge">
                    <span>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!s.include_in_all_subject_challenge}
                            onChange={(e) =>
                              onToggle(s.id, e.target.checked)
                            }
                            disabled={saving}
                          />
                        }
                        label={
                          s.include_in_all_subject_challenge
                            ? "Included"
                            : "Excluded"
                        }
                      />
                    </span>
                  </Tooltip>
                  {saving && <CircularProgress size={18} />}
                </Stack>
              </TableCell>

              <TableCell>
                {s.updated_at
                  ? new Date(s.updated_at).toLocaleString()
                  : "—"}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/* =========================================================
   ADMIN LEADERBOARD MANAGER
   ========================================================= */
export default function AdminLeaderboardManager() {
  const [tab, setTab] = useState(0);

  // Leaderboards
  const [loadingBoards, setLoadingBoards] = useState(true);
  const [subjectAll, setSubjectAll] = useState([]);
  const [mathOnly, setMathOnly] = useState([]);
  const [postUtme, setPostUtme] = useState([]);

  // Settings
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [savingMap, setSavingMap] = useState({});

  const loadLeaderboards = async () => {
    setLoadingBoards(true);
    try {
      const [subAll, math, utme] = await Promise.all([
        api.get("/leaderboard/weekly?exam_type=SUBJECT_PRACTICE"),
        api.get("/leaderboard/weekly?exam_type=SUBJECT_PRACTICE&subject_id=2"),
        api.get("/leaderboard/weekly?exam_type=POST_UTME"),
      ]);

      setSubjectAll(subAll.data?.leaderboard || []);
      setMathOnly(math.data?.leaderboard || []);
      setPostUtme(utme.data?.leaderboard || []);
    } catch (err) {
      console.error("Admin leaderboard load error:", err);
    } finally {
      setLoadingBoards(false);
    }
  };

  const loadSubjectSettings = async () => {
    setLoadingSettings(true);
    try {
      const res = await api.get("/leaderboard/admin/subject-settings");
      setSubjects(res.data?.subjects || []);
    } catch (err) {
      console.error("Admin subject settings load error:", err);
      setSubjects([]);
    } finally {
      setLoadingSettings(false);
    }
  };

  const refreshAll = async () => {
    await loadLeaderboards();
    await loadSubjectSettings();
  };

  useEffect(() => {
    refreshAll();
  }, []);

  const handleToggle = async (subjectId, checked) => {
    const prev = subjects;

    setSubjects((curr) =>
      curr.map((s) =>
        s.id === subjectId
          ? { ...s, include_in_all_subject_challenge: checked }
          : s
      )
    );

    setSavingMap((m) => ({ ...m, [subjectId]: true }));

    try {
      await api.put(`/leaderboard/admin/subject-settings/${subjectId}`, {
        include_in_all_subject_challenge: checked,
      });
      await loadSubjectSettings();
    } catch (err) {
      console.error("Toggle update failed:", err);
      setSubjects(prev);
    } finally {
      setSavingMap((m) => {
        const next = { ...m };
        delete next[subjectId];
        return next;
      });
    }
  };

  return (
    <Box>
      {/* HEADER */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={900}>
            🛠 Leaderboard Manager (Admin)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor weekly leaderboards and control subject inclusion dynamically
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<ReplayIcon />}
          onClick={refreshAll}
          sx={{ fontWeight: 800 }}
        >
          Refresh
        </Button>
      </Stack>

      {/* TABS */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<LeaderboardIcon />} iconPosition="start" label="Monitor" />
          <Tab icon={<SettingsIcon />} iconPosition="start" label="Subject Inclusion" />
        </Tabs>
      </Paper>

      {/* MONITOR TAB */}
      {tab === 0 && (
        <Paper sx={{ p: 2 }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" fontWeight={900} sx={{ mb: 1 }}>
                📘 Subject Practice (All)
              </Typography>
              {loadingBoards ? (
                <CircularProgress />
              ) : (
                <LeaderboardTable rows={subjectAll} />
              )}
            </Box>

            <Box>
              <Typography variant="h6" fontWeight={900} sx={{ mb: 1 }}>
                📐 Mathematics Only
              </Typography>
              {loadingBoards ? (
                <CircularProgress />
              ) : (
                <LeaderboardTable rows={mathOnly} />
              )}
            </Box>

            <Box>
              <Typography variant="h6" fontWeight={900} sx={{ mb: 1 }}>
                🎯 Post-UTME
              </Typography>
              {loadingBoards ? (
                <CircularProgress />
              ) : (
                <LeaderboardTable rows={postUtme} />
              )}
            </Box>
          </Stack>
        </Paper>
      )}

      {/* SUBJECT INCLUSION TAB */}
      {tab === 1 && (
        <Paper sx={{ p: 2 }}>
          {loadingSettings ? (
            <Box textAlign="center" py={5}>
              <CircularProgress />
              <Typography mt={2}>Loading subject settings…</Typography>
            </Box>
          ) : (
            <SubjectInclusionSettings
              subjects={subjects}
              savingMap={savingMap}
              onToggle={handleToggle}
            />
          )}
        </Paper>
      )}
    </Box>
  );
}
